import type { InstallationType } from "@/base/sensor-configurations"
import type { PayloadAction, Reducer } from "@reduxjs/toolkit"

import {
  addDeviceAsync,
  createCustomAutomationAsync,
  createCustomNotificationAsync,
  customizeNotificationStringAsync,
  deleteEventActionTriggersAsync,
  isDeviceInTrigger as isDeviceInConnection,
  removeDeviceFromFarmAsync,
  replaceDeviceAsync,
  updateDeviceConnectionAsync,
} from "@/base/farmhq-api"
import { loadActiveFarmAsync } from "@/base/load-app"
import * as Models from "@/redux/models"
import { createSelector, createSlice, isAnyOf } from "@reduxjs/toolkit"

import { formatDeviceSelectOption } from "@/redux/device-configurations.reducer"
import type { RootState } from "@/redux/root.reducer"
import { clearSelectedTrigger, clickCancel } from "./actions"
import { signOutAsync } from "./auth.reducer"
import { createCachedSelector } from "re-reselect"

export type TriggerFilterKey = "automation" | "notification" | "pair"

/**
 * If true, the device can be used as a 'Target Device' in a trigger/connection.
 */
export function isValidConnectionTarget(device: {
  deviceInstallationType: InstallationType | null | undefined
}) {
  return (
    device.deviceInstallationType === "pump" ||
    device.deviceInstallationType === "pump_off_only" ||
    device.deviceInstallationType === "pump_on_off" ||
    device.deviceInstallationType === "pump_vfd" ||
    device.deviceInstallationType === "valve"
  )
}

const adapter = Models.deviceConnection.adapter

const initialState: Models.ModelState<Models.EventActionTrigger> & {
  selectedId?: number
} = {
  entities: {},
  ids: [],
}

const slice = createSlice({
  extraReducers: (builder) => {
    return builder
      .addCase(updateDeviceConnectionAsync.pending, (state, { meta }) => {
        if (typeof meta.arg.notify === "boolean") {
          adapter.updateOne(state, {
            changes: { notify: meta.arg.notify },
            id: meta.arg.connectionId,
          })
        }
      })
      .addCase(
        customizeNotificationStringAsync.fulfilled,
        (state, { meta }) => {
          adapter.updateOne(state, {
            changes: { notificationString: meta.arg.notificationString },
            id: meta.arg.triggerId,
          })
        },
      )
      .addCase(loadActiveFarmAsync.fulfilled, (state, { payload }) => {
        adapter.setAll(state, payload.triggers ?? [])
      })
      .addCase(deleteEventActionTriggersAsync.fulfilled, (state, { meta }) => {
        adapter.removeMany(state, meta.arg.ids)
      })
      .addCase(addDeviceAsync.fulfilled, (state, { payload }) => {
        if (payload.status === "VALID" && payload.deviceConnections) {
          adapter.upsertMany(state, payload.deviceConnections)
        }
      })
      .addCase(replaceDeviceAsync.fulfilled, (state, { meta, payload }) => {
        if (payload.status === "VALID") {
          if (payload.deviceConnections) {
            adapter.upsertMany(state, payload.deviceConnections)
          }
          adapter.removeMany(
            state,
            Object.values(state.entities)
              .filter((entity) => {
                return isDeviceInConnection({
                  connection: entity,
                  device: { deviceId: meta.arg.deviceIdOld },
                })
              })
              .map((entity) => {
                return entity.id
              }),
          )
        }
      })
      .addCase(removeDeviceFromFarmAsync.fulfilled, (state, { meta }) => {
        adapter.removeMany(
          state,
          state.ids.filter((id): boolean => {
            const entity = state.entities[id]
            return (
              entity?.sourceDeviceId === meta.arg.deviceId ||
              entity?.targetDeviceId === meta.arg.deviceId
            )
          }),
        )
      })
      .addMatcher(
        isAnyOf(
          createCustomAutomationAsync.fulfilled,
          createCustomNotificationAsync.fulfilled,
        ),
        (state, { payload }) => {
          adapter.upsertOne(state, payload)
        },
      )
      .addMatcher(
        isAnyOf(
          clickCancel,
          clearSelectedTrigger,
          createCustomAutomationAsync.fulfilled,
          createCustomNotificationAsync.fulfilled,
          deleteEventActionTriggersAsync.fulfilled,
          customizeNotificationStringAsync.fulfilled,
        ),
        (state) => {
          state.selectedId = undefined
        },
      )
      .addMatcher(isAnyOf(signOutAsync.fulfilled), () => ({ ...initialState }))
  },
  initialState,
  name: "triggers",
  reducers: {
    selectTrigger(state, { payload }: PayloadAction<number>) {
      state.selectedId = payload
    },
  },
})
const deviceConnections: Reducer<typeof initialState> = slice.reducer

export default deviceConnections

export const { selectTrigger } = slice.actions
export const getAllDeviceSelectOptions = createSelector(
  Models.deviceConfiguration.selectAll,
  (configurations) =>
    configurations.map((config) => ({
      label: formatDeviceSelectOption(config),
      value: config.deviceId,
    })),
)

export const getConnectionRoleForDeviceId = (
  state: RootState,
  initiatorDeviceId: string,
) => {
  const config = Models.deviceConfiguration.selectById(
    state,
    initiatorDeviceId,
  )
  if (config) {
    if (isValidConnectionTarget(config)) {
      return "target"
    }
    return "source"
  }
  return undefined
}

const deviceIdInput = (_state: RootState, deviceId: string) => deviceId
export const getConnectionsByDeviceIdFromState = createCachedSelector(
  [
    Models.deviceConnection.selectAll,
    Models.deviceConfiguration.selectEntities,
    deviceIdInput,
  ],
  function getConnectionsByDeviceIdFromState(connections, devices, deviceId) {
    return connections
      .filter((connection) =>
        isDeviceInConnection({
          connection,
          device: { deviceId },
        }),
      )
      .map((connection) => {
        const sourceDevice = devices[connection.sourceDeviceId]
        const targetDevice =
          typeof connection.targetDeviceId === "string"
            ? devices[connection.targetDeviceId]
            : null

        return {
          ...connection,
          role:
            sourceDevice?.deviceId === deviceId
              ? ("source" as const)
              : ("target" as const),
          sourceDeviceName: sourceDevice?.deviceName,
          targetDeviceName: targetDevice?.deviceName,
        }
      })
  },
)(deviceIdInput)
